// src/components/Admin/Settings/Tax/TaxAddEditForm.jsx
import React, { useState, useEffect } from "react";
import { X, Save, Percent, Calendar, Globe2, MapPin, Info, ToggleLeft, ToggleRight, Star } from "lucide-react";
import apiClient from "@/services/api/client";

export default function TaxAddEditForm({ onSave, onClose, item }) {
  const [formData, setFormData] = useState(
    item || {
      name: "",
      rate: "",
      centerGstRate: "",
      stateGstRate: "",
      baseRate: "",
      startDate: "",
      country: "",
      state: "",
      isActive: true,
      isDefault: false,
    }
  );

  // Country and State dropdowns
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState(""); // For fetching states

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const res = await apiClient.get("/domain/countries");
        const data = res?.data || res || [];
        const countryList = Array.isArray(data) ? data.filter(c => c.active !== false) : [];
        setCountries(countryList);

        // If editing and country name exists, find and set the country code
        if (item?.country && countryList.length > 0) {
          const found = countryList.find(c => c.name === item.country || c.code === item.country);
          if (found) {
            setSelectedCountryCode(found.code);
          }
        }
      } catch (err) {
        console.error("Failed to fetch countries:", err);
        setCountries([]);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, [item]);

  // Fetch states when country code changes
  useEffect(() => {
    const fetchStates = async () => {
      if (!selectedCountryCode) {
        setStates([]);
        return;
      }
      setLoadingStates(true);
      try {
        const res = await apiClient.get(`/domain/states?countryCode=${selectedCountryCode}`);
        const data = res?.data || res || [];
        setStates(Array.isArray(data) ? data.filter(s => s.active !== false) : []);
      } catch (err) {
        console.error("Failed to fetch states:", err);
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    };
    fetchStates();
  }, [selectedCountryCode]);

  // Handle country selection - store name, track code separately
  const handleCountryChange = (countryCode) => {
    const country = countries.find(c => c.code === countryCode);
    setSelectedCountryCode(countryCode);
    handleChange("country", country?.name || "");
    handleChange("state", ""); // Reset state
  };

  // Handle state selection - store name
  const handleStateChange = (stateCode) => {
    const state = states.find(s => s.code === stateCode);
    handleChange("state", state?.name || "");
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!String(formData.name).trim()) {
      alert("Tax Name is required");
      return;
    }
    if (!String(formData.rate).trim()) {
      alert("Rate is required");
      return;
    }

    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Teal themed */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Percent className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {item ? "Edit Tax" : "Add Tax"}
              </h2>
              <p className="text-xs text-teal-100">
                Configure tax rate and GST details
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-6 space-y-5">
            {/* Basic Info Section */}
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider pb-2 border-b border-gray-200">
              Tax Information
            </h3>

            <div className="grid grid-cols-3 gap-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Tax Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="e.g., GST 18%"
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Percent className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Effective From
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleChange("startDate", e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* isActive Toggle */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Status
                </label>
                <button
                  type="button"
                  onClick={() => handleChange("isActive", !formData.isActive)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all ${
                    formData.isActive
                      ? "bg-green-50 border-green-300 text-green-700"
                      : "bg-gray-50 border-gray-200 text-gray-500"
                  }`}
                >
                  <span className="text-sm font-medium">
                    {formData.isActive ? "Active" : "Inactive"}
                  </span>
                  {formData.isActive ? (
                    <ToggleRight className="w-6 h-6" />
                  ) : (
                    <ToggleLeft className="w-6 h-6" />
                  )}
                </button>
              </div>
            </div>

            {/* Default Tax Toggle */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => handleChange("isDefault", !formData.isDefault)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${
                  formData.isDefault
                    ? "bg-amber-50 border-amber-300"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${formData.isDefault ? 'bg-amber-100' : 'bg-gray-100'}`}>
                    <Star className={`w-4 h-4 ${formData.isDefault ? 'text-amber-600 fill-amber-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="text-left">
                    <p className={`text-sm font-medium ${formData.isDefault ? 'text-amber-700' : 'text-gray-700'}`}>
                      Set as Default Tax
                    </p>
                    <p className="text-xs text-gray-500">
                      Applied when no tax is configured at item or category level
                    </p>
                  </div>
                </div>
                {formData.isDefault ? (
                  <ToggleRight className="w-6 h-6 text-amber-600" />
                ) : (
                  <ToggleLeft className="w-6 h-6 text-gray-400" />
                )}
              </button>
            </div>

            {/* Rate Section */}
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider pb-2 border-b border-gray-200 mt-6">
              Tax Rates
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Rate */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Total Rate <span className="text-red-500">*</span>
                  <span className="relative group">
                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      Total tax rate applied to items (e.g., 18%)
                    </span>
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.rate}
                    onChange={(e) => handleChange("rate", e.target.value)}
                    placeholder="18.00"
                    required
                    className="w-full px-4 py-2.5 pr-8 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                </div>
              </div>

              {/* CGST */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  CGST
                  <span className="relative group">
                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      Central GST - Goes to Central Government
                    </span>
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.centerGstRate}
                    onChange={(e) => handleChange("centerGstRate", e.target.value)}
                    placeholder="9.00"
                    className="w-full px-4 py-2.5 pr-8 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                </div>
              </div>

              {/* SGST */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  SGST
                  <span className="relative group">
                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      State GST - Goes to State Government
                    </span>
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.stateGstRate}
                    onChange={(e) => handleChange("stateGstRate", e.target.value)}
                    placeholder="9.00"
                    className="w-full px-4 py-2.5 pr-8 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                </div>
              </div>

              {/* Base Rate */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Base Rate
                  <span className="relative group">
                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      Base rate before any additional taxes
                    </span>
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.baseRate}
                    onChange={(e) => handleChange("baseRate", e.target.value)}
                    placeholder="0.00"
                    className="w-full px-4 py-2.5 pr-8 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                </div>
              </div>
            </div>

            {/* GST Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                <strong>For GST:</strong> Total Rate should equal CGST + SGST. For example, GST 18% = CGST 9% + SGST 9%
              </p>
            </div>

            {/* Location Section */}
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider pb-2 border-b border-gray-200 mt-6">
              Applicable Region
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Country */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Country
                </label>
                <div className="relative">
                  <select
                    value={selectedCountryCode}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    disabled={loadingCountries}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all appearance-none disabled:bg-gray-100"
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Globe2 className="w-4 h-4 text-gray-400" />
                  </div>
                  {loadingCountries && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              {/* State */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  State
                </label>
                <div className="relative">
                  <select
                    value={states.find(s => s.name === formData.state)?.code || ""}
                    onChange={(e) => handleStateChange(e.target.value)}
                    disabled={!selectedCountryCode || loadingStates}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all appearance-none disabled:bg-gray-100"
                  >
                    <option value="">{selectedCountryCode ? "Select State" : "Select Country First"}</option>
                    {states.map((state) => (
                      <option key={state.code} value={state.code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <MapPin className="w-4 h-4 text-gray-400" />
                  </div>
                  {loadingStates && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-all flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {item ? "Update Tax" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
